import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApiService } from '../services/authApi';
import { User, LoginResponse, UserBranch, UserMenuPermission } from '../types/user';

interface AuthContextType {
  user: User | null;
  userBranches: UserBranch[];
  userMenuPermissions: UserMenuPermission[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [userBranches, setUserBranches] = useState<UserBranch[]>([]);
  const [userMenuPermissions, setUserMenuPermissions] = useState<UserMenuPermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const checkAuth = async () => {
      try {
        const storedUser = authApiService.getUserInfo();
        const hasToken = authApiService.isAuthenticated();
        
        if (storedUser && hasToken) {
          // Set user immediately from localStorage
          setUser(storedUser);
          setUserBranches(authApiService.getUserBranches());
          setUserMenuPermissions(authApiService.getUserMenuPermissions());
          
          // Validate token in background (non-blocking)
          // If validation fails, it will be caught on actual API calls
          authApiService.validateToken().catch(error => {
            // console.warn('Background token validation failed:', error);
            // Don't logout here - let actual API calls handle 401/403
          });
        }
      } catch (error) {
        // console.error('Auth check failed:', error);
        // Only logout if we can't even read from localStorage
        if (!authApiService.getUserInfo() || !authApiService.getAuthToken()) {
          authApiService.logout();
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      const response = await authApiService.authenticateUser(username, password);
      
      if (response.success && response.data) {
        // Extract user data from the new response structure
        const loginData: LoginResponse = response.data;
        
        // Store the user data in localStorage to prevent context loss
        // Token is already stored by authApiService.authenticateUser
        if (loginData.userInfo && loginData.userInfo.user) {
          const userWithDefaultBranch: User = {
            ...loginData.userInfo.user,
            defaultBranchId:
              loginData.userInfo.user.defaultBranchId ??
              loginData.userInfo.defaultBranchId,
          };
          authApiService.storeUserInfo(userWithDefaultBranch);
          authApiService.storeUserBranches(loginData.userInfo.userBranches || []);
          authApiService.storeUserMenuPermissions(loginData.userInfo.userMenuPermissions || []);
          
          // Set user and related data in context
          setUser(userWithDefaultBranch);
          setUserBranches(loginData.userInfo.userBranches || []);
          setUserMenuPermissions(loginData.userInfo.userMenuPermissions || []);
          
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    authApiService.logout();
    setUser(null);
    setUserBranches([]);
    setUserMenuPermissions([]);
  };

  const refreshAuth = async () => {
    // No refresh token mechanism - JWT tokens expire after 24 hours
    // When expired, users must log in again
    // This function is kept for API compatibility but does nothing
    // console.warn('refreshAuth called but refresh tokens are not supported. User must log in again when token expires.');
  };

  const value: AuthContextType = {
    user,
    userBranches,
    userMenuPermissions,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
